import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import configs from 'configs';
import { PRISTINE } from 'utils/constants/queryStatuses';
import { getFinanceParams } from './utils';
import { PUBLIC_PROGRAMS_STORE_KEY, PUBLIC_PROFILE_STORE_KEY } from './actionTypes';
import {
  updatePublicFinanceProfileAction,
  fetchPublicMatchedFinanceProgramsAction,
  setPublicFinanceProfileAction,
} from './actions';

const { isProduction } = configs;

const createFinanceProfile = () => {
  console.error('createFinanceProfile is not available');
  if (!isProduction) {
    throw new Error();
  }
};

export const usePublicFinanceSupport = ({ user, isEnabled }) => {
  const dispatch = useDispatch();

  const {
    status: financeProfileStatus = PRISTINE, data: financeProfile,
  } = useSelector((state) => state.keyValueStore[PUBLIC_PROFILE_STORE_KEY]) ?? {};
  const {
    status: matchedFinanceProgramsStatus = PRISTINE, data: matchedFinancePrograms,
  } = useSelector((state) => state.keyValueStore[PUBLIC_PROGRAMS_STORE_KEY]) ?? {};

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    dispatch(setPublicFinanceProfileAction({ user }));
  }, [dispatch, user, isEnabled, financeProfile]);

  useEffect(() => {
    if (!isEnabled || !financeProfile || !user) {
      return;
    }
    const { aidTypes } = financeProfile;
    const financeParams = getFinanceParams(user, financeProfile, aidTypes);
    dispatch(fetchPublicMatchedFinanceProgramsAction({ financeParams }));
  }, [dispatch, financeProfile, isEnabled, user]);

  const updateFinanceProfile = useCallback((data) => {
    dispatch(updatePublicFinanceProfileAction({ data }));
  }, [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFinanceProfileDebounced = useCallback(debounce(updateFinanceProfile, 500), [updateFinanceProfile]);

  return {
    updateFinanceProfile,
    updateFinanceProfileDebounced,
    createFinanceProfile,
    financeProfileStatus,
    financeProfile,
    matchedFinancePrograms,
    matchedFinanceProgramsStatus,
  };
};
